import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Users, Award } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { PARTNERS } from "@/data/content";

const WHY = [
  { Icon: TrendingUp, title: "Measurable impact", text: "Transparent reporting on placements, mentoring hours and outcomes — you'll always know exactly what your support achieved." },
  { Icon: Users, title: "First access to talent", text: "Meet driven, resilient, multilingual young people before the market does. Our partners consistently hire from the students they host." },
  { Icon: Award, title: "Real engagement", text: "Your people mentor, host and coach — the kind of hands-on purpose that staff surveys can't buy." },
];

export default function Partners() {
  return (
    <main data-testid="partners-page">
      <section className="bg-[#0B132B] text-white pt-40 pb-24 md:pb-32 grain relative overflow-hidden" data-testid="partners-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <Reveal>
            <Overline light>Partners & Sponsors</Overline>
            <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-4xl">
              Our partners don't just fund the mission. <span className="text-[#FF5A36]">They are the mission.</span>
            </h1>
            <p className="text-white/70 max-w-2xl mt-8 text-lg leading-relaxed">
              Every workplace door, every mentor hour, every first placement exists because an
              organisation decided access was worth investing in.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="partners-logo-grid">
        <Reveal>
          <Overline>Our Network</Overline>
          <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight text-[#0B132B] mb-4">
            Our partners and sponsors
          </h2>
          <p className="text-[#4A5568] max-w-xl mb-16">Proudly supported by the organisations opening real doors for our students.</p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          {PARTNERS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div className="group bg-white border border-black/5 rounded-[1.5rem] aspect-[16/9] flex items-center justify-center p-10 hover:border-[#FF5A36]/40 hover:-translate-y-1 transition-[transform,border-color] duration-500" data-testid={`partner-logo-${i}`}>
                <img src={p.logo} alt={`${p.name} logo`} className="max-h-24 md:max-h-28 w-auto max-w-full object-contain grayscale group-hover:grayscale-0 transition-[filter] duration-500" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white py-24 md:py-32" data-testid="partners-why-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <Overline>Why Partner With IBY</Overline>
            <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight text-[#0B132B] max-w-3xl mb-16">
              This isn't branding exposure. It's a stake in what happens next.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.12}>
                <div className="bg-[#FAF8F5] rounded-[2rem] p-10 h-full border border-black/5">
                  <div className="w-14 h-14 rounded-2xl bg-[#FF5A36]/10 flex items-center justify-center mb-8">
                    <w.Icon size={26} className="text-[#FF5A36]" />
                  </div>
                  <h3 className="font-display font-semibold text-2xl tracking-tight text-[#0B132B] mb-4">{w.title}</h3>
                  <p className="text-[#4A5568] leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="partners-cta">
        <Reveal>
          <div className="rounded-[2.5rem] bg-[#FF5A36] text-white p-12 md:p-20 grain relative overflow-hidden">
            <div className="relative z-10 max-w-3xl">
              <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight leading-tight">
                One conversation could open a hundred doors.
              </h2>
              <p className="mt-5 text-white/85 text-lg leading-relaxed max-w-xl">
                Whether you can fund a program, host placements, or lend your people as mentors —
                let's talk about what access looks like from your side of the door.
              </p>
              <Link
                to="/contact"
                data-testid="partners-contact-btn"
                className="inline-flex items-center gap-2 mt-10 rounded-full bg-[#0B132B] text-white px-8 py-4 font-bold hover:bg-black transition-colors duration-300"
              >
                Start the Conversation <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
